import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { KeyRound, MailCheck, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/motion";

const inputCls = "w-full border border-girder bg-steel px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:border-hivis focus:outline-none";
const labelCls = "mb-1.5 block font-mono text-[11px] uppercase tracking-widest text-slate-500";

function RequestLink() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/request-setup", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  if (sent) {
    return (
      <div className="clip-corner border border-girder bg-obsidian p-8 text-center" data-testid="setup-sent-panel">
        <MailCheck className="mx-auto h-10 w-10 text-hivis" />
        <h3 className="mt-4 font-display text-2xl font-bold uppercase text-slate-100">Check your inbox</h3>
        <p className="mt-3 text-sm leading-relaxed text-slate-400">
          If that email matches a dealer account, a setup link is on its way. The link works once and expires in 24 hours.
        </p>
        <Link to="/dealer-portal" data-testid="setup-back-to-login" className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-hivis">
          Back to dealer login <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="clip-corner border border-girder bg-obsidian p-6 sm:p-8" data-testid="setup-request-form">
      <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-hivis"><MailCheck className="h-3.5 w-3.5" /> Email me a setup link</p>
      <p className="mt-3 text-sm text-slate-400">Enter the email your dealership registered with. We'll send a secure, one-time link to create your password.</p>
      <div className="mt-5">
        <label className={labelCls} htmlFor="su-email">Email</label>
        <input id="su-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="setup-email" placeholder="you@signshop.com" />
      </div>
      <button type="submit" disabled={busy} data-testid="setup-request-submit" className="clip-corner mt-6 w-full bg-hivis py-3.5 font-display text-lg font-bold uppercase tracking-wide text-obsidian transition-colors hover:bg-hivis-hover disabled:opacity-60">
        {busy ? "Sending..." : "Send Setup Link"}
      </button>
    </form>
  );
}

function SetPassword({ token }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/set-password", { token, password });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="clip-corner border border-hivis/40 bg-obsidian p-8 text-center" data-testid="setup-done-panel">
        <KeyRound className="mx-auto h-10 w-10 text-hivis" />
        <h3 className="mt-4 font-display text-2xl font-bold uppercase text-slate-100">Password set</h3>
        <p className="mt-3 text-sm text-slate-400">Your dealer account is active. Log in to unlock wholesale pricing.</p>
        <Link to="/dealer-portal" data-testid="setup-go-login" className="clip-corner mt-6 inline-flex items-center gap-2 bg-hivis px-8 py-3.5 font-display font-bold uppercase text-obsidian transition-colors hover:bg-hivis-hover">
          Go to dealer login <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="clip-corner border border-girder bg-obsidian p-6 sm:p-8" data-testid="set-password-form">
      <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-hivis"><KeyRound className="h-3.5 w-3.5" /> Create your password</p>
      <div className="mt-5 space-y-4">
        <div>
          <label className={labelCls} htmlFor="sp-pass">New password</label>
          <input id="sp-pass" name="new-password" autoComplete="new-password" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="set-password-input" placeholder="Minimum 8 characters" />
        </div>
        <div>
          <label className={labelCls} htmlFor="sp-confirm">Confirm password</label>
          <input id="sp-confirm" name="confirm-password" autoComplete="new-password" type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} data-testid="set-password-confirm" placeholder="••••••••" />
        </div>
      </div>
      <button type="submit" disabled={busy} data-testid="set-password-submit" className="clip-corner mt-6 w-full bg-hivis py-3.5 font-display text-lg font-bold uppercase tracking-wide text-obsidian transition-colors hover:bg-hivis-hover disabled:opacity-60">
        {busy ? "Saving..." : "Set Password"}
      </button>
    </form>
  );
}

export default function DealerSetupPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  return (
    <div data-testid="dealer-setup-page">
      <PageHeader
        kicker="Dealer Setup"
        title={token ? "Create your password" : "Activate your account"}
        sub={token ? "One step and you're in." : "For approved dealers activating a new account, or anyone who needs a fresh password."}
        image="/assets/img/w-gallery-img-5882.jpg"
      />
      <section className="mx-auto max-w-xl px-4 py-16 sm:px-6 lg:py-24">
        <Reveal>{token ? <SetPassword token={token} /> : <RequestLink />}</Reveal>
      </section>
    </div>
  );
}
